import Typography from '@mui/material/Typography';
import { useGoals } from 'features/goals/hooks/use-goals';
import { useLocalizedLifeAreas } from 'features/life-areas/hooks/use-localized-life-areas';
import { useSortedTags } from 'features/tags';
import { useUpdateUserSettings } from 'features/user';
import { defaultFilter } from 'pages/today/constants';
import { useTodayFilterDocument } from 'pages/today/hooks/use-today-filter-document';
import React, { useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Calendar18, Plus, SettingsSort18 } from 'shared/assets/icons';
import { TopBarBase } from 'shared/components/connected/top-bar-base';
import { FilterButton } from 'shared/components/ui/filter-button';
import { Colors, IconButton } from 'shared/components/ui/icon-button';
import { MobileTodoNavigation } from 'shared/components/ui/mobile-todo-navigation';
import { useOpenCreateTask } from 'shared/contexts/task-detail/use-open-create-task';
import { useToday } from 'shared/contexts/today';
import { useActivePath } from 'shared/hooks/use-active-path';
import { useOpenMenu } from 'shared/hooks/use-open-menu';
import { Paths } from 'shared/routes';
import {
  DocumentOrganizationKeys,
  FilterDocument,
  FilterOptionKeys,
} from 'shared/types/filter-document';
import {
  GroupByMode,
  SortingMode,
  TaskGroupBy,
  TodayGroupBy,
} from 'shared/types/sorting-mode';
import { useTheme } from 'styled-components';

import { MainContextMenu } from './main-context-menu';
import * as Styled from './page-top-bar.style';
import { SearchButton } from './search-button';
import { UserAvatar } from './user-avatar';

export type PageTopBarProps = {
  onCalendar?: () => void;
  calendarOpen?: boolean;
  totalDurationLabel?: string;
};

const supportedFilters = [FilterOptionKeys.Tag];
const supportedGroupByModes: TaskGroupBy[] = [
  GroupByMode.Deadline,
  GroupByMode.Goal,
  GroupByMode.LifeArea,
  GroupByMode.Priority,
  GroupByMode.Type,
  GroupByMode.DayPart,
];
const supportedSortingModes = [
  SortingMode.Custom,
  SortingMode.DueDate,
  SortingMode.Priority,
];

export const PageTopBar: React.FC<PageTopBarProps> = ({
  onCalendar,
  calendarOpen,
  totalDurationLabel,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const activePath = useActivePath({ level: 2 });
  const updateUserSettings = useUpdateUserSettings();
  const today = useToday();

  const tags = useSortedTags();
  const goals = useGoals();
  const lifeAreas = useLocalizedLifeAreas();
  const { menuOpen, openMenu, closeMenu } = useOpenMenu();
  const buttonRef = useRef<HTMLButtonElement>(null);

  const filterDocument = useTodayFilterDocument();
  const openCreateTask = useOpenCreateTask();

  const defaultFilterDocument = useMemo(() => defaultFilter(today), [today]);

  const onCreate = useCallback(
    () =>
      openCreateTask({
        endStrategy: { deadline: new Date(), completionCount: null },
      }),
    [openCreateTask],
  );

  const updateFilterDocument = useCallback(
    (newFilterDocument: FilterDocument) =>
      updateUserSettings({
        filters: {
          today: newFilterDocument,
        },
        // support for old iOS ordering
        todoSortingMode: newFilterDocument.sortingMode ?? SortingMode.DueDate,
        todayGroupBy:
          newFilterDocument.groupByMode === GroupByMode.Goal
            ? TodayGroupBy.Goal
            : newFilterDocument.groupByMode === GroupByMode.LifeArea
              ? TodayGroupBy.LifeArea
              : newFilterDocument.groupByMode === GroupByMode.Priority
                ? TodayGroupBy.Priority
                : newFilterDocument.groupByMode === GroupByMode.Type
                  ? TodayGroupBy.Type
                  : newFilterDocument.groupByMode === GroupByMode.DayPart
                    ? TodayGroupBy.DayPart
                    : TodayGroupBy.None ?? TodayGroupBy.None,
        todayFilterByTags:
          newFilterDocument[DocumentOrganizationKeys.Filters]?.[
            FilterOptionKeys.Tag
          ]?.include ?? [],
      }),
    [updateUserSettings],
  );

  const onReset = useCallback(() => {
    updateUserSettings({
      filters: {
        today: defaultFilterDocument,
      },
    });
  }, [defaultFilterDocument, updateUserSettings]);

  return (
    <TopBarBase
      mobileNavigationSlot={
        <MobileTodoNavigation activePath={activePath as Paths} />
      }
    >
      <Styled.Container>
        <Styled.Title>
          <Typography variant="h5" component="h1">
            {t('pages.today.title')}
            {totalDurationLabel && (
              <Styled.Duration>{totalDurationLabel}</Styled.Duration>
            )}
          </Typography>
        </Styled.Title>

        <Styled.Interactions>
          {theme.isMobile && <SearchButton />}
          {!theme.isMobile && (
            <IconButton
              icon={Plus}
              onClick={onCreate}
              color={Colors.Default}
              preventDrag
              highlighted={false}
            />
          )}
          <FilterButton
            filterDocument={filterDocument}
            defaultFilterDocument={defaultFilterDocument}
            goals={goals}
            lifeAreas={lifeAreas}
            tags={tags}
            updateFilterDocument={updateFilterDocument}
            onReset={onReset}
            supportedFilters={supportedFilters}
            supportedGroupByModes={supportedGroupByModes}
            supportedSortingModes={supportedSortingModes}
            labelOverrides={{
              [GroupByMode.Deadline]: t(
                `context-menus.filtering.task.grouping-and-sorting.grouping.options.${GroupByMode.None}`,
              ),
            }}
            renderAsIconButton
            preventDrag
          />
          {!theme.isMobile && (
            <IconButton
              ref={buttonRef}
              icon={SettingsSort18}
              onClick={openMenu}
              color={Colors.Default}
              highlighted={menuOpen}
              preventDrag
            />
          )}
          {menuOpen && (
            <MainContextMenu onClose={closeMenu} referenceElement={buttonRef} />
          )}
          {!theme.isMobile && <Styled.Divider />}
          {!theme.isMobile && (
            <IconButton
              icon={Calendar18}
              onClick={onCalendar}
              highlighted={calendarOpen}
              color={Colors.Default}
              preventDrag
            />
          )}
          {theme.isMobile && <UserAvatar />}
        </Styled.Interactions>
      </Styled.Container>
    </TopBarBase>
  );
};
